import { useState } from "react";
import { CheckCircle, Clock } from "lucide-react";
import { dailyRhythm } from "@/lib/content";
import { useLocation } from "@/contexts/LocationContext";

// Real stills from the Lynnwood home walkthrough video (no stock). Keyed to the
// `photo` field on each dailyRhythm slot. Seattle has no footage yet, so its
// day-in-the-life renders copy-only rather than borrowing Lynnwood's images.
import diningStill from "@/assets/photos-real/dining.jpg";
import deckStill from "@/assets/photos-real/deck.jpg";
import gardenStill from "@/assets/photos-real/garden.jpg";

const photoMap: Record<string, string> = {
  garden: gardenStill,
  dining: diningStill,
  deck: deckStill,
};

/**
 * WarmActivities — "A Day in the Life" daily-rhythm showcase for the homepage.
 *
 * WHY: The reference sites either describe activities in plain text (Dawit) or
 * show them in a generic photo carousel (Aspira). This section *shows and tells*:
 * a Morning / Afternoon / Evening segmented control swaps a real home photo
 * alongside the concrete activities for that part of the day—answering the
 * "what is life actually like here?" question directly.
 *
 * Brand-safe: theme tokens only (--accent, --primary, --card, radius, shadows),
 * Playfair headings + Source Sans body, and the same fade/scale motion language
 * used elsewhere on the site. Photos and copy are real (drawn from existing data).
 */
export function WarmActivities() {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = dailyRhythm[activeIndex];
  const { isSeattle } = useLocation();
  // Only the Lynnwood home has real footage; never show its rooms on Seattle.
  const photo = isSeattle ? undefined : photoMap[active.photo];

  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <div className="text-center">
          <span className="eyebrow text-accent-strong">A Day in the Life</span>
          <h2 className="mt-4 display-md font-heading font-semibold tracking-[-0.025em] text-foreground">
            What life{" "}
            <span className="font-display-italic font-normal text-accent-strong">looks like</span>{" "}
            here
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            From a gentle morning to a calm, cared-for evening—a look at the warm,
            purposeful rhythm of each day in our home.
          </p>
        </div>

        {/* Segmented time-of-day control */}
        <div
          role="tablist"
          aria-label="Time of day"
          className="mx-auto mt-10 flex max-w-xl flex-wrap justify-center gap-2 rounded-full bg-muted p-1.5"
        >
          {dailyRhythm.map((slot, i) => {
            const isActive = i === activeIndex;
            return (
              <button
                key={slot.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveIndex(i)}
                className={`inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                  isActive
                    ? "bg-card text-primary shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <slot.icon className="h-4 w-4" />
                {slot.label}
              </button>
            );
          })}
        </div>

        {/* Active slot: photo + activity list. `key` retriggers the fade on change. */}
        <div
          key={active.id}
          className={`mx-auto mt-10 grid items-center gap-8 duration-500 animate-in fade-in slide-in-from-bottom-2 ${
            photo ? "max-w-5xl md:grid-cols-2" : "max-w-2xl"
          }`}
        >
          {/* Photo — real still, Lynnwood only. */}
          {photo ? (
            <div className="relative aspect-[4/3] overflow-hidden rounded-3xl shadow-[var(--shadow-card)]">
              <img
                src={photo}
                alt={`${active.label} at our home — ${active.headline}`}
                className="h-full w-full object-cover"
              />
              <span className="absolute left-4 top-4 rounded-full bg-card/90 px-3 py-1 text-xs font-semibold text-primary shadow-sm backdrop-blur-sm">
                {active.timeRange}
              </span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 self-start rounded-full bg-accent/10 px-4 py-1.5 text-sm font-semibold text-accent-strong">
              <Clock className="h-4 w-4" />
              {active.timeRange}
            </div>
          )}

          {/* Copy + activities */}
          <div>
            <h3 className="font-heading text-2xl font-semibold text-foreground">
              {active.headline}
            </h3>
            <p className="mt-3 leading-relaxed text-muted-foreground">{active.blurb}</p>
            <ul className="mt-6 space-y-3">
              {active.activities.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-accent-strong" />
                  <span className="text-sm text-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
